import React, { useEffect, useState } from 'react'
import {
    Autocomplete, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useDispatch, useSelector } from 'react-redux'
import { getBanksAc, getLesseeAc, getOwnerListApi } from '../../../shared_elements/actionCreators'
import { globalGetService, globalPostService } from '../../../utils/globalApiServices'
import { checkApiStatus, getGlobalCookie } from '../../../utils'
import { getSummaryInvoiceInitApi, addSummaryInvoiceApi } from '../apiService'
import { useSnackbar } from 'notistack'
import { PageLoader } from '../../../shared_elements'
import NoRecordFound from '../../../shared_elements/NoRecordFound'
import moment from 'moment'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NumericLabel from 'react-pretty-numbers'
import DeleteIcon from '@mui/icons-material/Delete';
import { backendDateFormat, displayDateFormatShort, fieldDateFormat } from '../../../constants'
import { trackActivity } from '../../../utils/mixpanel'


const invoiceTypeOptions = [{ label: 'Reserve', value: 1 }, { label: 'Rental', value: 2 }]

const TruncatedTooltip = ({ title, children }) => {
    const ref = React.useRef(null)
    const [truncated, setTruncated] = React.useState(false)
    return (
        <Tooltip title={truncated ? title : ''} placement="top" arrow>
            <div
                ref={ref}
                onMouseEnter={() => setTruncated(ref.current?.scrollWidth > ref.current?.clientWidth)}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {children}
            </div>
        </Tooltip>
    )
}

const getCsvIds = (values) => {
    if (!Array.isArray(values) || values.length === 0) return null
    const ids = values
        .map(item => item?.id)
        .filter(id => id !== null && id !== undefined && id !== '')
    return ids.length ? ids.join(',') : null
}

const GenerateSummaryInvoice = ({ setGenerateSummary, generateSummary, getResponseBack }) => {
    const dispatch = useDispatch()
    const { lessee, banks, owner } = useSelector((state) => state.leases)
    const { enqueueSnackbar } = useSnackbar()
    const [isLoading, setLoading] = useState(false)
    const [summaries, setSummaries] = useState({})
    const [invoices, setInvoice] = useState([])
    const [isSummaryDialogOpen, setSummaryDialogOpen] = useState(false)
    const [error, setError] = useState({})
    const [dropDownLoader, setDropDownLoader] = useState({ lessee: false, owner: false, banks: false })
    const [selectedGroups, setSelectedGroups] = useState(new Set())
    const lessorAccess = JSON.parse(getGlobalCookie('lessorAccess') || 'null');

    useEffect(() => {
        if (!lessee?.length) {
            setDropDownLoader(l => ({ ...l, lessee: true }))
            dispatch(getLesseeAc({}))
        }
        if (!owner?.length) {
            setDropDownLoader(l => ({ ...l, owner: true }))
            dispatch(getOwnerListApi({}))
        }
        if (!banks?.length) {
            setDropDownLoader(l => ({ ...l, banks: true }))
            dispatch(getBanksAc({ lessor_id: lessorAccess?.id, show_all: true }))
        }
    }, [])

    useEffect(() => {
        if (Array.isArray(lessee)) setDropDownLoader(l => ({ ...l, lessee: false }))
        if (Array.isArray(owner)) setDropDownLoader(l => ({ ...l, owner: false }))
        if (Array.isArray(banks)) setDropDownLoader(l => ({ ...l, banks: false }))
    }, [lessee, owner, banks])

    const onGenerateSummary = () => {
        const query = {
            invoice_type: summaries.invoice_types?.value || null,
            lessee_id: getCsvIds(summaries?.lessee_ids),
            lessor_id: getCsvIds(summaries?.lessor_ids),
            bank_id: getCsvIds(summaries?.bank_ids),
            start_date: summaries.start_date || null,
            end_date: summaries.end_date || null,
            invoice_date: summaries.date || null,
            due_date: summaries.due_date || null,
        }
        setLoading(true)
        globalGetService(getSummaryInvoiceInitApi(query))
            .then(response => {
                setLoading(false)
                if (checkApiStatus(response)) {
                    setInvoice(response?.data?.data?.groups || [])
                    setGenerateSummary(false)
                    setSummaryDialogOpen(true)
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } })
                }
            })
    }

    const toggleGroupSelection = (index) => {
        setSelectedGroups(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const generateSumInvoiceApi = () => {
        const validGroups = invoices.filter((s, i) => s.invoices.length > 1 && (selectedGroups.size === 0 || selectedGroups.has(i)));
        const payload = {
            groups: validGroups.map(summary => ({ invoice_slugs: summary.invoices.map(inv => inv.slug) }))
        }
        setLoading(true)
        globalPostService(addSummaryInvoiceApi(), payload)
            .then(response => {
                setLoading(false)
                if (checkApiStatus(response)) {
                    enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } })
                    setSummaryDialogOpen(false)
                    setSummaries({})
                    setInvoice([])
                    setSelectedGroups(new Set())
                    getResponseBack()
                    trackActivity('Item Added', { page_title: `Generate Summary Invoice`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: payload, response: response?.data })
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } })
                    trackActivity('Item Added', { page_title: `Generate Summary Invoice`, error_msg: response?.data?.message, item_id: null, payload: payload, response: response?.data })
                }
            })
    }

    const onFieldChange = (keyParam, value) => {
        setSummaries(prev => ({ ...prev, [keyParam]: value }))
    }

    const handleRemoveInvoice = (summaryIndex, invoiceIndex) => {
        const updated = [...invoices]
        updated[summaryIndex].invoices.splice(invoiceIndex, 1)
        if (updated[summaryIndex].invoices.length === 0) {
            updated.splice(summaryIndex, 1)
            setSelectedGroups(prev => {
                const next = new Set();
                prev.forEach(i => {
                    if (i < summaryIndex) next.add(i);
                    else if (i > summaryIndex) next.add(i - 1);
                });
                return next;
            });
        }
        setInvoice(updated)
    }

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Generate Summary Invoice', page_type: page_type })
    }

    const handleRemoveGroup = (summaryIndex) => {
        setInvoice(prev => prev.filter((_, i) => i !== summaryIndex))
        setSelectedGroups(prev => {
            const next = new Set();
            prev.forEach(i => {
                if (i < summaryIndex) next.add(i);
                else if (i > summaryIndex) next.add(i - 1);
            });
            return next;
        });
    }

    return (
        <>
            {isLoading ? <PageLoader /> : generateSummary &&
                <Dialog className='lease-management-modal' maxWidth='md' fullWidth open={true}>
                    <DialogTitle>Generate Summary Invoice's</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    options={lessee || []}
                                    multiple
                                    size='small'
                                    disabled={dropDownLoader.lessee}
                                    getOptionLabel={option => option?.name || ''}
                                    value={summaries.lessee_ids || []}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(e, value) => onFieldChange('lessee_ids', value)}
                                    renderInput={params =>
                                        <Tooltip title={params?.inputProps?.value?.length > 23 ? params.inputProps.value : ''} placement="bottom" arrow>
                                            <TextField {...params} label="Lessee"
                                                InputLabelProps={{ shrink: true }} variant="outlined"
                                                slotProps={{ input: { ...params.InputProps, endAdornment: <>{dropDownLoader.lessee ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> } }}
                                            />
                                        </Tooltip>}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    options={owner || []}
                                    multiple
                                    size='small'
                                    disabled={dropDownLoader.owner}
                                    getOptionLabel={option => option?.name || ''}
                                    value={summaries.lessor_ids || []}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(e, value) => onFieldChange('lessor_ids', value)}
                                    renderInput={params =>
                                        <Tooltip title={params?.inputProps?.value?.length > 23 ? params.inputProps.value : ''} placement="bottom" arrow>
                                            <TextField {...params} label="Lessor/Holdco" 
                                                InputLabelProps={{ shrink: true }} variant="outlined"
                                                slotProps={{ input: { ...params.InputProps, endAdornment: <>{dropDownLoader.owner ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> } }}
                                            />
                                        </Tooltip>}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    options={banks || []}
                                    multiple
                                    size='small'
                                    disabled={dropDownLoader.banks}
                                    getOptionLabel={option => option?.account_number ? `${option.name} (${option.account_number})` : option?.name || ''}
                                    value={summaries.bank_ids || []}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(e, value) => onFieldChange('bank_ids', value)}
                                    renderOption={(props, option) => ( <li {...props}> {option.name} {option.account_number && ( <span style={{ fontSize: '0.75em', marginLeft: 4, color: 'inherit' }}> ({option.account_number}) </span> )} </li> )}
                                    renderInput={params =>
                                        <Tooltip title={params?.inputProps?.value?.length > 23 ? params.inputProps.value : ''} placement="bottom" arrow>
                                            <TextField {...params} label="Bank Name"
                                                InputLabelProps={{ shrink: true }} variant="outlined"
                                                slotProps={{ input: { ...params.InputProps, endAdornment: <>{dropDownLoader.banks ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> } }}
                                            />
                                        </Tooltip>}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    options={invoiceTypeOptions}
                                    getOptionLabel={option => option?.label || ''}
                                    value={summaries.invoice_types || null}
                                    onChange={(e, value) => onFieldChange('invoice_types', value)}
                                    size='small'
                                    renderInput={params =>
                                        <TextField {...params} label="Invoice Type"
                                            InputLabelProps={{ shrink: true }} variant="outlined" />}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Period Start Date"
                                        value={summaries?.start_date ? moment(summaries.start_date) : null}
                                        maxDate={moment()}
                                        onChange={(data) => {
                                            onFieldChange("start_date", data ? moment(data).format(backendDateFormat) : null);
                                        }}
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.start_date || false,
                                                helperText: error.start_date || '',
                                            },
                                            field: {
                                                clearable: true,
                                            }
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Period End Date"
                                        value={summaries?.end_date ? moment(summaries.end_date) : null}
                                        onChange={(data) => {
                                            onFieldChange("end_date", data ? moment(data).format(backendDateFormat) : null);
                                        }}
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.end_date || false,
                                                helperText: error.end_date || '',
                                            },
                                            field: {
                                                clearable: true,
                                            }
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Invoice Date"
                                        value={summaries?.date ? moment(summaries.date) : null}
                                        onChange={(data) => {
                                            onFieldChange("date", data ? moment(data).format(backendDateFormat) : null);
                                        }}
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.date || false,
                                                helperText: error.date || '',

                                            },
                                            field: {
                                                clearable: true,

                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Due Date"
                                        value={summaries?.due_date ? moment(summaries.due_date) : null}
                                        onChange={(data) => {
                                            onFieldChange("due_date", data ? moment(data).format(backendDateFormat) : null);
                                        }}
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.due_date || false,
                                                helperText: error.due_date || '',
                                            },
                                            field: {
                                                clearable: true,

                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ marginRight: "15px" }}>
                        <Button size='small' variant="outlined" color="primary"
                            onClick={() => { setGenerateSummary(false); setSummaries({}); setError({}); handleItemClicked('Cancel Button') }}>
                            Cancel
                        </Button>
                        <Button size='small' variant='contained' color='primary' disabled={isLoading}
                            onClick={() => { onGenerateSummary(); handleItemClicked('Proceed Button') }}>
                            Proceed
                        </Button>
                    </DialogActions>
                </Dialog>
            }

            {isSummaryDialogOpen &&
                (isLoading ? <PageLoader /> :
                    <Dialog className='lease-management-modal' open={true} maxWidth={!invoices.length || invoices.every(s => s.invoices.length <= 1) ? 'sm' : 'md'} fullWidth>
                        <DialogTitle>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ArrowBackIcon
                                    onClick={() => { setSummaryDialogOpen(false); setGenerateSummary(true); setSummaries({}); setInvoice([]); setSelectedGroups(new Set()); handleItemClicked('Back Icon') }}
                                    color='primary'
                                    style={{ cursor: 'pointer', marginRight: '3px' }}
                                />
                                Grouping of Summary Invoices
                            </div>
                            {(() => {
                                const validGroups = invoices.filter(s => s.invoices.length > 1);
                                const totalGroups = validGroups.length;
                                const totalRental = validGroups.filter(s => s.invoice_type?.name === 'Rental').reduce((acc, s) => acc + s.invoices.length, 0);
                                const totalReserve = validGroups.filter(s => s.invoice_type?.name === 'Reserve').reduce((acc, s) => acc + s.invoices.length, 0);
                                return totalGroups > 0 ? (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, display: 'block', mt: '2px', ml:'25px' }}>
                                        Total Summary Invoices: {totalGroups}, Total Rental Invoices: {totalRental}, Total Reserve Invoices: {totalReserve}
                                    </Typography>
                                ) : null;
                            })()}
                        </DialogTitle>
                        <DialogContent dividers sx={{ overflowX: 'hidden' }}>
                            {!Array.isArray(invoices) || !invoices.length || invoices.every(s => s.invoices.length <= 1)
                                ? <NoRecordFound />
                                : invoices.map((summary, index) =>
                                    summary.invoices.length > 1 && (
                                        <TableContainer key={index} component={Paper} style={{ marginBottom: '10px', padding: '8px', maxWidth:"835px", border: selectedGroups.has(index) ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)', borderRadius: '4px' }}>
                                            <Grid container spacing={2} style={{ marginBottom: '10px', width: '100%' }}>
                                                <Grid size={{ md: 1, sm: 6, xs: 12 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedGroups.has(index)}
                                                        onChange={() => { toggleGroupSelection(index); handleItemClicked('Group Selected Checkbox') }}
                                                        color="primary"
                                                    />
                                                </Grid>
                                                <Grid size={{ md: 2, sm: 6, xs: 12 }}>
                                                    <Typography variant="subtitle2" color='primary'>Lessee:</Typography>
                                                    <TruncatedTooltip title={summary.lessee?.name || ''}>
                                                        <Typography variant="body2" component="span">{summary.lessee?.name || '-'}</Typography>
                                                    </TruncatedTooltip>
                                                </Grid>
                                                <Grid size={{ md: 2, sm: 6, xs: 12 }}>
                                                    <Typography variant="subtitle2" color='primary'>Lessor/Holdco:</Typography>
                                                    <TruncatedTooltip title={summary.lessor?.name || ''}>
                                                        <Typography variant="body2" component="span">{summary.lessor?.name || '-'}</Typography>
                                                    </TruncatedTooltip>
                                                </Grid>
                                                <Grid size={{ md: 2, sm: 6, xs: 12 }}>
                                                    <Typography variant="subtitle2" color='primary'>Bank Name:</Typography>
                                                    <TruncatedTooltip title={summary.bank_name || ''}>
                                                        <Typography variant="body2" component="span">{summary.bank_name || '-'}</Typography>
                                                    </TruncatedTooltip>
                                                </Grid>
                                                <Grid size={{ md: 2, sm: 6, xs: 12 }}>
                                                    <Typography variant="subtitle2" color='primary'>Invoice Type:</Typography>
                                                    <Typography variant="body2">{summary.invoice_type?.name || '-'}</Typography>
                                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                                                        {summary.invoices.some(inv => inv.is_advanced === true) && <Chip label="Advanced" size="small" color="primary" variant="outlined" sx={{ fontSize: '9px', height: '16px' }} />}
                                                        {summary.invoices.some(inv => inv.is_arrears === true) && <Chip label="Arrears" size="small" color="primary" variant="outlined" sx={{ fontSize: '9px', height: '16px' }} />}
                                                    </div>
                                                </Grid>
                                                <Grid size={{ md: 2, sm: 6, xs: 12 }}>
                                                    <Typography variant="subtitle2" color='primary'>Invoice Period:</Typography>
                                                    <Typography variant="body2">
                                                        {summary.start_date ? moment(summary.start_date).format(displayDateFormatShort) : '-'}
                                                        {summary.end_date ? ` – ${moment(summary.end_date).format(displayDateFormatShort)}` : ''}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ md: 1, sm: 6, xs: 12 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Tooltip  arrow title='Remove Card'>
                                                        <DeleteIcon
                                                            fontSize='small'
                                                            color="error"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => { handleRemoveGroup(index); handleItemClicked('Remove Card Icon') }}
                                                        />
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                            <Table className='mui-table-format'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Asset</TableCell>
                                                        <TableCell>Invoice Number</TableCell>
                                                        <TableCell>Amount ({summary.currency === 'USD' ? 'US$' : '€'})</TableCell>
                                                        <TableCell>Invoice Date</TableCell>
                                                        <TableCell>Due Date</TableCell>
                                                        <TableCell></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {summary.invoices.map((invoice, invoiceIndex) => (
                                                        <TableRow key={invoiceIndex}>
                                                            <TableCell sx={{ maxWidth: '130px' }}>
                                                                <TruncatedTooltip title={invoice.asset?.asset_name || ''}>
                                                                    {invoice.asset?.asset_name}
                                                                </TruncatedTooltip>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: '150px' }}>
                                                                <TruncatedTooltip title={invoice.invoice_number || ''}>
                                                                    {invoice.invoice_number}
                                                                </TruncatedTooltip>
                                                            </TableCell>
                                                            <TableCell>
                                                                <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency || summary?.currency, precision: 2, commafy: true, justification: 'L' }}>
                                                                    {invoice.total_amount}
                                                                </NumericLabel>
                                                            </TableCell>
                                                            <TableCell>{invoice.invoice_date ? moment(invoice.invoice_date).format(displayDateFormatShort) : '-'}</TableCell>
                                                            <TableCell>{invoice.due_date ? moment(invoice.due_date).format(displayDateFormatShort) : '-'}</TableCell>
                                                            <TableCell>
                                                                <Tooltip arrow title='Remove'>
                                                                    <CancelRoundedIcon
                                                                        fontSize='small'
                                                                        color="error"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => { handleRemoveInvoice(index, invoiceIndex); handleItemClicked('Remove Invoice Icon') }}
                                                                    />
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            {summary.invoices.length === 2 &&
                                                <p style={{ background: 'yellow', padding: '2px', marginTop: '8px', fontSize: '12px', display: 'inline-block' }}>
                                                    Note: To generate summary invoice minimum linked invoices should be 2 or more.
                                                </p>
                                            }
                                        </TableContainer>
                                    )
                                )
                            }
                        </DialogContent>
                        <DialogActions sx={{ marginRight: "15px" }}>
                            <Button size='small' variant='outlined' color='primary'
                                onClick={() => { setSummaryDialogOpen(false); setSummaries({}); setInvoice([]); setSelectedGroups(new Set()); handleItemClicked('Close Button') }}>
                                Close
                            </Button>
                            <Button size='small' variant='contained' color='primary'
                                disabled={isLoading || !invoices.length}
                                onClick={() => { generateSumInvoiceApi(); handleItemClicked('Generate Summary Invoice Button') }}>
                                {isLoading ? <CircularProgress color="inherit" size={24} /> : selectedGroups.size > 0 ? `Generate Summary Invoice ( ${selectedGroups.size} )` : 'Generate Summary Invoice'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )
            }
        </>
    )
}

export default GenerateSummaryInvoice
